<script lang="ts" setup>
import { ref, provide, toRef, computed } from 'vue';

import EvaluationsManualTasksModal from './EvaluationsManualTasksModal.vue';
import EvaluationsManualTasksModalEdit from './EvaluationsManualTasksModalEdit.vue';
import { useField, useFieldArray, useFormErrors, useValidateField } from 'vee-validate';
import {
  EvaluationParameterDto,
  instanceOfEnumerationEvaluationParameterDto,
  NumericEvaluationParameterDto,
  ParameterCombinationTableDto,
} from '@/api/generator';
import EvaluationsManualTasksEnumTile from './EvaluationsManualTasksEnumTile.vue';
import { Sortable } from 'sortablejs-vue3';
import { SortableEvent, SortableOptions } from 'sortablejs';
import { FormMode } from '@/components/forms/types';
import EvaluationsQuantityTile, { EvaluationDisplayOptions } from './EvaluationsQuantityTile.vue';
import { clone } from 'lodash';
import { useToggle } from '@vueuse/core';
import InlineValidationError from '@/components/forms/InlineValidationError/InlineValidationError.vue';
import Scope from '@/components/evaluation/scopes/Scope.vue';
import Button from '@/components/buttons/Button.vue';
import PtwDeleteButton from '@/components/buttons/PtwDeleteButton.vue';
import { mdiDragHorizontal, mdiPlus } from '@mdi/js';
import VeeTextField from '@/components/forms/TextField/VeeTextField.vue';
import VeeCheckbox from '@/components/forms/Checkbox/VeeCheckbox.vue';
import { scopeDisplayName } from '../utils';
import { getVeeValidationErrorsForPaths } from '@/components/forms/utils';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import IndicatorLabel from '@/components/Tabs/IndicatorLabel.vue';
import { TabIndicatorState } from '@/components/Tabs/types';

export type ScopeDisplayOptions = {
  editable: 'enabled' | 'disabled';
  sorting: 'enabled' | 'disabled';
  toggle: 'enabled' | 'disabled';
  evaluations: EvaluationDisplayOptions;
};

const props = defineProps<
  {
    name: string; // evaluationScopes[{index = 0}]
    mode: FormMode;
    targetSystemId?: string;
    combinationTable?: ParameterCombinationTableDto;
    onDelete?: () => void;
  } & ScopeDisplayOptions
>();

provide(
  'targetSysId',
  toRef(() => props.targetSystemId),
);

const [createOverlayOpen, toggleCreateOverlayOpen] = useToggle(false);

const editOverlayOpen = ref(false);

function toggleModalHandler() {
  toggleCreateOverlayOpen();
}

const validate = useValidateField(toRef(() => props.name));

// Registers the scope's own path, validated on mount: vee-validate otherwise hoists its errors onto an
// ancestor path state and drops them while that is unvalidated.
useField(
  toRef(() => props.name),
  undefined,
  { validateOnMount: true },
);

const formErrors = useFormErrors();

// Errors of the scope itself, its condition and its evaluations, whether or not the user touched them.
const indicatorMessages = computed(() =>
  getVeeValidationErrorsForPaths(formErrors.value, [props.name]),
);
const hasErrors = computed(() => indicatorMessages.value.length > 0);
const indicatorState = computed(() => (hasErrors.value ? TabIndicatorState.ERROR : undefined));

const { value: evaluationScopeName } = useField<string>(toRef(() => `${props.name}.name`));
const evaluationScopeTitle = computed(() => scopeDisplayName(evaluationScopeName.value));

const validateParams = useValidateField(toRef(() => `${props.name}.evaluationParameters`));

const {
  fields: evaluationParameters,
  push,
  remove,
  move: moveEvaluationParameter,
  update: updateEvaluationParameter,
} = useFieldArray<EvaluationParameterDto>(toRef(() => `${props.name}.evaluationParameters`));

async function pushEvaluationParameter(param: EvaluationParameterDto) {
  push(param);
  await validate();
  await validateParams();
}

async function removeEvaluationParameter(index: number) {
  remove(index);
  await validate();
  await validateParams();
}

function sortingEndHandler(e: SortableEvent) {
  if (e.oldIndex !== undefined && e.newIndex !== undefined) {
    moveEvaluationParameter(e.oldIndex, e.newIndex);
  }
}

const editIndex = ref<number>();

const editNumericEvaluationParameterDto = ref<NumericEvaluationParameterDto | undefined>();

const editNumericEvaluationParameterDtoHandler = (
  dto: NumericEvaluationParameterDto,
  myIndex: number,
) => {
  editIndex.value = myIndex;
  editOverlayOpen.value = true;
  editNumericEvaluationParameterDto.value = dto;
};

const sortableOptions = computed<SortableOptions>(() => ({
  handle: '.handle',
  disabled: props.mode === 'details',
  scroll: true,
  scrollSensitivity: 300,
  forceFallback: true,
}));
</script>

<template>
  <v-expansion-panel>
    <template #title>
      <div class="d-flex align-center ga-2 w-100">
        <div
          v-if="mode !== 'details' && sorting === 'enabled'"
          class="d-flex align-center align-self-stretch"
        >
          <v-icon :icon="mdiDragHorizontal" class="ml-2 scopeHandle cursor-move" />
        </div>

        <VeeCheckbox
          v-if="toggle === 'enabled'"
          :name="`${name}.isEnabled`"
          :placeholder="$t('Required')"
          :mode="mode"
          :disabled="mode === 'details'"
          @click.stop
        />

        <Tooltip v-if="indicatorState">
          <template #target="{ props: tooltipProps }">
            <IndicatorLabel
              v-bind="tooltipProps"
              :title="evaluationScopeTitle"
              :indicatorState="indicatorState"
            />
          </template>
          <template #tooltip>
            <div v-for="message in indicatorMessages" :key="message">{{ message }}</div>
          </template>
        </Tooltip>
        <IndicatorLabel v-else :title="evaluationScopeTitle" />

        <PtwDeleteButton
          v-if="editable === 'enabled' && mode !== 'details' && onDelete"
          class="ml-auto"
          @click.stop
          @delete="onDelete"
        />
      </div>
    </template>
    <template #text>
      <InlineValidationError :name="name" ignoreTouched class="inline-error dense mb-4" />

      <VeeTextField
        v-if="mode !== 'details' && editable === 'enabled'"
        :name="`${name}.name`"
        :label="$t('Name')"
        :displayConverter="scopeDisplayName"
        class="mb-4"
      />

      <Scope
        :name="`${name}.condition`"
        :combinationTable="combinationTable"
        :editable="editable"
        :mode="mode"
      />

      <Button
        v-if="mode !== 'details' && evaluations.editable === 'enabled'"
        variant="secondary"
        size="small"
        :icon="mdiPlus"
        iconPosition="start"
        :label="$t('AddEvaluation')"
        :disabled="!targetSystemId"
        class="mb-4"
        @click="toggleModalHandler"
      />

      <InlineValidationError
        :name="`${name}.evaluationParameters`"
        ignoreTouched
        class="inline-error dense mb-4"
      />

      <v-expansion-panels multiple class="d-block">
        <Sortable
          class="d-flex flex-column ga-2"
          :list="evaluationParameters"
          :options="sortableOptions"
          itemKey="key"
          @end="sortingEndHandler"
        >
          <template #item="{ element: myField, index }">
            <EvaluationsManualTasksEnumTile
              v-if="instanceOfEnumerationEvaluationParameterDto(myField.value)"
              :name="`${name}.evaluationParameters[${index}]`"
              :isTrackItImport="evaluations.isTrackItImport"
              @remove="removeEvaluationParameter(index)"
            />

            <EvaluationsQuantityTile
              v-else
              :name="`${name}.evaluationParameters[${index}]`"
              v-bind="evaluations"
              @remove="removeEvaluationParameter(index)"
              @edit="editNumericEvaluationParameterDtoHandler($event, index)"
            />
          </template>
        </Sortable>
      </v-expansion-panels>
    </template>
  </v-expansion-panel>

  <EvaluationsManualTasksModal
    v-model:overlayOpen="createOverlayOpen"
    @add="pushEvaluationParameter"
  />

  <EvaluationsManualTasksModalEdit
    v-model:overlayOpen="editOverlayOpen"
    :dtoToEdit="editNumericEvaluationParameterDto"
    @editEvaluationParameterDto="
      (paramDto: NumericEvaluationParameterDto) => {
        if (editIndex !== undefined && editIndex !== -1) {
          updateEvaluationParameter(editIndex, clone(paramDto));
        }
      }
    "
  />
</template>
